import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Trans, useTranslation } from 'react-i18next'
import { useStripe, useElements, CardCvcElement } from '@stripe/react-stripe-js'

import Icon from '../../Icons'
import DateField from '../../FormLegacy/FormFieldsV2/DateField'
import ErrorPaymentModal from '../ErrorPaymentModal'
import SuccessConfirmationModal from '../../Popups/SuccessConfirmationModal'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import API from '../../../api/api'

import ModifyPrice from './ModifyPrice'
import CreditCardContent from './CreditCardContent'

const PaymentModal = ({
  isOpen,
  userRole,
  buildingId,
  handleClose,
  handleSubmit,
  purchaseItemTitle,
  purchaseItemPrice,
}) => {
  const { t } = useTranslation(['payment', 'common'])
  const MAX_PERCENTAGE = 100
  const MAX_ITEM_PRICE = purchaseItemPrice

  const [isLoading, setLoading] = useState(false)
  const [isLoadingBuildingCard, setLoadingBuildingCard] = useState(false)
  const [invoiceConfirmModalVisible, setInvoiceConfirmModalVisible] = useState(false)
  const [errorPaymentModalVisible, setErrorPaymentModalVisible] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('creditCard')
  const [modifyPriceMethod, setModifyPriceMethod] = useState(null)
  const [prefferedPriceOrDiscount, setPrefferedPriceOrDiscount] = useState('')
  const [discountType, setDiscountType] = useState('flatRate')
  const [startDate, setStartDate] = useState(new Date())

  const { displaySnackbar } = useSnackbar()

  const stripe = useStripe()
  const elements = useElements()
  const [buildingCreditCard, setBuildingCreditCard] = useState(null)
  const [cardOwnerFullName, setCardOwnerFullName] = useState(null)
  const [cardData, setCardData] = useState({
    cardNumber: false,
    cardCvc: false,
    cardExpiry: false,
  })

  const cardDataVerify = useMemo(() => {
    if (cardData.cardExpiry && cardData.cardNumber && cardData.cardCvc && cardOwnerFullName) {
      return true
    } else {
      return false
    }
  }, [cardData, cardOwnerFullName])

  const verifyCardDataChanges = (event) => {
    if (event.complete) {
      setCardData({ ...cardData, [event.elementType]: true })
    } else {
      setCardData({ ...cardData, [event.elementType]: false })
    }
  }

  const onChangePrefferedPriceOrDiscount = (textValue) => {
    const value = Number(textValue)

    if (!modifyPriceMethod) return null
    if (modifyPriceMethod === 'customPrice') {
      setPrefferedPriceOrDiscount(value)
    } else if (discountType === 'flatRate') {
      if (value <= MAX_ITEM_PRICE) {
        setPrefferedPriceOrDiscount(value)
      } else {
        setPrefferedPriceOrDiscount(MAX_ITEM_PRICE)
      }
    } else {
      if (value <= MAX_PERCENTAGE) {
        setPrefferedPriceOrDiscount(value)
      } else {
        setPrefferedPriceOrDiscount(MAX_PERCENTAGE)
      }
    }
  }

  const getTotalPrice = () => {
    if (modifyPriceMethod === 'customPrice') {
      return prefferedPriceOrDiscount || purchaseItemPrice
    } else if (modifyPriceMethod === 'discount' && discountType === 'flatRate') {
      return purchaseItemPrice - prefferedPriceOrDiscount
    } else if (modifyPriceMethod === 'discount' && discountType === 'percentage') {
      return (purchaseItemPrice - purchaseItemPrice * (prefferedPriceOrDiscount / 100)).toFixed(2)
    } else {
      return purchaseItemPrice
    }
  }

  const getCreditCardToken = async () => {
    const data = {
      name: cardOwnerFullName,
    }
    try {
      const cardElement = elements.getElement(CardCvcElement)
      const { token } = await stripe.createToken(cardElement, data)
      return token.id
    } catch (err) {
      displaySnackbar(err.response.data.message, true)
    }
  }

  const handleSubmitPayment = async () => {
    setLoading(true)
    const sendData = {
      byInvoice: paymentMethod === 'byInvoice',
    }

    if (modifyPriceMethod === 'customPrice' && prefferedPriceOrDiscount) {
      sendData.customPrice = prefferedPriceOrDiscount
    } else if (modifyPriceMethod === 'discount') {
      sendData.discountValue = prefferedPriceOrDiscount
      sendData.discountType = discountType === 'flatRate' ? 'amount_off' : 'percent_off'
    }

    if (sendData.byInvoice && userRole === 'PropertyManager') {
      const date = moment(startDate).startOf('day')
      const startDateTime = date.valueOf()
      sendData.startDate = startDateTime
    } else if (!sendData.byInvoice && !buildingCreditCard) {
      const token = await getCreditCardToken()
      sendData.cardToken = token
    }

    try {
      await handleSubmit(sendData)
      if (sendData.byInvoice && userRole === 'PropertyManager') {
        setInvoiceConfirmModalVisible(true)
      } else {
        handleClose()
      }
    } catch {
      setErrorPaymentModalVisible(true)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (modifyPriceMethod) {
      onChangePrefferedPriceOrDiscount(prefferedPriceOrDiscount)
    }
  }, [modifyPriceMethod, discountType])

  useEffect(() => {
    if (buildingId) {
      setLoading(false)
      setLoadingBuildingCard(true)
      API.getBuildingCreditCardData(buildingId)
        .then(({ data }) => {
          if (data.last4) {
            setBuildingCreditCard(data)
          }
          setLoadingBuildingCard(false)
        })
        .catch((err) => {
          setLoadingBuildingCard(false)
          console.error(err)
        })
    }
  }, [buildingId, isOpen])

  if (invoiceConfirmModalVisible) {
    return (
      <SuccessConfirmationModal
        header={t('payment:invoiceConfirmation.title')}
        description={
          <div>
            <Trans
              i18nKey="payment:invoiceConfirmation.description"
              components={[<span key="0" />]}
              values={{ email: 'hello@elevatedliving.com' }}
            />
          </div>
        }
        handleClose={() => {
          setInvoiceConfirmModalVisible(false)
          handleClose()
        }}
      />
    )
  }

  if (!isOpen) return null

  return (
    <div className="payment-modal">
      <div className="payment-modal__container">
        <div className="window-header">
          <div className="left-side">
            <div className="title">{t('payment:header.title')}</div>
          </div>
          <div className="right-side">
            <div className="navi-button" onClick={handleClose}>
              <Icon icon="cross" />
            </div>
          </div>
        </div>
        <div className="main-view">
          <h1>{t('payment:method.title')}</h1>
          <div className="payment-method-selectors">
            <div
              className={`payment-method ${
                paymentMethod === 'creditCard' && 'active'
              } ${isLoading && 'disabled'}`}
              onClick={() => setPaymentMethod('creditCard')}
            >
              {t('payment:method.creditCard')}
            </div>
            <div
              className={`payment-method ${paymentMethod === 'byInvoice' && 'active'} ${
                isLoading && 'disabled'
              }`}
              onClick={() => setPaymentMethod('byInvoice')}
            >
              {t('payment:method.payByInvoice')}
            </div>
          </div>
          <ModifyPrice
            userRole={userRole}
            modifyPriceMethod={modifyPriceMethod}
            isLoading={isLoading}
            prefferedPriceOrDiscount={prefferedPriceOrDiscount}
            discountType={discountType}
            setModifyPriceMethod={setModifyPriceMethod}
            onChangePrefferedPriceOrDiscount={onChangePrefferedPriceOrDiscount}
            setDiscountType={setDiscountType}
          />
          {paymentMethod === 'creditCard' ? (
            <CreditCardContent
              isLoading={isLoading}
              isLoadingBuildingCard={isLoadingBuildingCard}
              buildingCreditCard={buildingCreditCard}
              cardOwnerFullName={cardOwnerFullName}
              verifyCardDataChanges={verifyCardDataChanges}
              setBuildingCreditCard={setBuildingCreditCard}
              setCardOwnerFullName={setCardOwnerFullName}
            />
          ) : paymentMethod === 'byInvoice' && userRole === 'PropertyManager' ? (
            <DateField
              name="date"
              label={t('payment:startDate.label')}
              placeholderText={t('payment:startDate.placeholder')}
              autoComplete="off"
              dateFormat="MM/dd/yyyy"
              minDate={new Date()}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onChangeRaw={(e) => e.preventDefault()}
              disabled={isLoading}
            />
          ) : null}
          <div className="purchase-summary">
            <h4>{t('payment:purchase.title')}</h4>
            <div className="flex-row">
              <div>{purchaseItemTitle}</div>
              <div className="bolded">
                ${purchaseItemPrice}
                {!modifyPriceMethod && t('payment:purchase.perMonthSuffix')}
              </div>
            </div>
            {modifyPriceMethod === 'discount' && (
              <React.Fragment>
                <hr />
                <div className="flex-row">
                  <div>{t('payment:discount')}</div>
                  <div className="bolded">
                    {discountType === 'flatRate'
                      ? `$${prefferedPriceOrDiscount || 0}`
                      : `${prefferedPriceOrDiscount || 0}%`}
                  </div>
                </div>
              </React.Fragment>
            )}
            {modifyPriceMethod && (
              <React.Fragment>
                <hr />
                <div className="flex-row">
                  <div>{t('payment:purchase.total')}</div>
                  <div className="bolded">
                    ${getTotalPrice()}
                    {t('payment:purchase.perMonthSuffix')}
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
        <div className="footer-view">
          <button className="button button-gray" onClick={handleClose} disabled={isLoading}>
            {t('common:actions.cancel')}
          </button>
          <button
            className="button button-primary"
            onClick={handleSubmitPayment}
            disabled={
              (paymentMethod === 'creditCard' &&
                !buildingCreditCard &&
                (!stripe || !elements || !cardDataVerify)) ||
              isLoading
            }
          >
            {paymentMethod === 'byInvoice'
              ? userRole === 'PropertyManager'
                ? t('payment:submit.sendRequest')
                : t('payment:submit.payByInvoice')
              : t('payment:submit.payAmount', { amount: getTotalPrice() })}
          </button>
        </div>
      </div>
      <ErrorPaymentModal
        isOpen={errorPaymentModalVisible}
        handleClose={() => setErrorPaymentModalVisible(false)}
        handleGoBack={() => {
          setErrorPaymentModalVisible(false)
          handleClose()
        }}
      />
    </div>
  )
}

PaymentModal.propTypes = {
  isOpen: PropTypes.bool,
  userRole: PropTypes.string,
  buildingId: PropTypes.number,
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  purchaseItemTitle: PropTypes.string,
  purchaseItemPrice: PropTypes.number,
}

export default PaymentModal
